import {
  afterNextRender,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import type { VivestSelectOption } from '@vivest-dev/design-system-web-components';
import { Steps } from '../../../shared/components/steps/steps';
import { HttpErrorResponse } from '@angular/common/http';
import { RegimeIr, Tributacao, TributacaoSimulada } from '../../../core/models/requerimento';
import { toTributacaoPayload } from '../../../core/models/requerimento-mapper';
import { LoaderService } from '../../../shared/services/loader-service';
import { RequerimentoService } from '../../../shared/services/requerimento-service';
import { RequerimentoState } from '../../../shared/services/requerimento-state';
import { ToastService } from '../../../shared/services/toast-service';
import { AntecipacaoResumo } from '../../../shared/utils/beneficio-resumo';
import {
  distribuirErrosDaApi,
  mensagemComDetalhe,
  ROTA_PAGINA_INDISPONIVEL,
  rotaDoErroRequerimento,
} from '../../../shared/utils/requerimento-erros';

export type RegimeImposto = 'progressivo' | 'regressivo';

const REGIME_POR_IR: Record<RegimeImposto, RegimeIr> = { progressivo: 'P', regressivo: 'R' };
const IR_POR_REGIME: Record<RegimeIr, RegimeImposto> = { P: 'progressivo', R: 'regressivo' };

const TITULO_POR_REGIME: Record<RegimeIr, string> = { P: 'Progressivo', R: 'Regressivo' };
const ORDEM_DOS_REGIMES: readonly RegimeIr[] = ['P', 'R'];

const MENSAGEM_ERRO_SALVAR = 'Não foi possível salvar a modalidade. Tente novamente.';
const MENSAGEM_ERRO_SIMULAR = 'Não foi possível calcular o Imposto de Renda.';
/**
 * Recusas do simulador que o backend repassa com o motivo (400/404/422), benefício da etapa 2 que a API não dá como
 * completo (409) e o simulador fora do ar (503): a tela fica, com o motivo no toast e o "Tentar novamente".
 */
const STATUS_SIMULACAO_NA_TELA = new Set([400, 404, 409, 422, 503]);

interface ModalidadeImpostoOpcao {
  value: RegimeImposto;
  titulo: string;
  /** IR da antecipação no mesmo regime do card; nulo sem antecipação. */
  antecipacao: AntecipacaoResumo | null;
  beneficioBrutoMensal: number;
  impostoRenda: number;
  valorLiquido: number;
}

/** Mesmo limite de QTD_DEPEND_IR no requerimento (0 a 20). */
const QUANTIDADE_MAXIMA_DEPENDENTES = 20;

const DEPENDENTES_OPTIONS: VivestSelectOption[] = Array.from(
  { length: QUANTIDADE_MAXIMA_DEPENDENTES + 1 },
  (_, quantidade) => ({ value: String(quantidade), label: String(quantidade) }),
);

const formatoMoeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
/** Só o número (ex.: 4.275,38), para quando o "R$" fica num span com tamanho menor. */
const formatoValor = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

interface AliquotaRegressiva {
  prazo: string;
  percentual: string;
}

// Mesmo conteúdo do comparativo do ModalidadeIr (instituto-online).
const ALIQUOTAS_REGRESSIVAS: readonly AliquotaRegressiva[] = [
  { prazo: 'Acima de 10 anos', percentual: '20%' },
  { prazo: 'De 8 a 10 anos', percentual: '20%' },
  { prazo: 'De 6 a 8 anos', percentual: '20%' },
  { prazo: 'de 4 a 6 anos', percentual: '25%' },
  { prazo: 'de 2 a 4 anos', percentual: '30%' },
  { prazo: 'Até 2 anos', percentual: '35%' },
];

/** Antecipação no regime do card: o bruto é o mesmo nos dois regimes; IR e líquido, os do regime. */
function antecipacaoDoRegime(
  simulacao: TributacaoSimulada | null,
  regime: RegimeIr,
  percentual: number | null,
): AntecipacaoResumo | null {
  const doRegime = simulacao?.regimes.find((item) => item.regime === regime);
  if (!simulacao || !doRegime || !percentual) return null;

  return {
    percentual,
    valorBruto: simulacao.valorBruto,
    valorImpostoRenda: doRegime.valorIr,
    valorLiquido: doRegime.valorLiquido,
  };
}

/** Espaço que o dialog do vivest-modal deixa em volta do painel (16px em cima e embaixo). */
const RESPIRO_DO_MODAL = 32;

/** Elemento do vivest-modal (Lit): `updateComplete` resolve depois que o componente renderiza. */
type VivestModalElement = HTMLElement & { updateComplete?: Promise<unknown> };

@Component({
  imports: [Steps],
  selector: 'app-modalidade-imposto',
  styleUrl: './modalidade-imposto.scss',
  templateUrl: './modalidade-imposto.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ModalidadeImposto implements OnInit {
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly loaderService = inject(LoaderService);
  private readonly requerimentoService = inject(RequerimentoService);
  private readonly requerimentoState = inject(RequerimentoState);
  private readonly toastService = inject(ToastService);
  private readonly modalComparativo = viewChild<ElementRef<VivestModalElement>>('modalComparativo');

  constructor() {
    // Se sair da tela com o modal aberto, a altura reservada não fica presa no body.
    inject(DestroyRef).onDestroy(() => this.liberarAlturaDoModal());
  }

  readonly stepAtual = signal(3);
  readonly totalSteps = signal(7);
  readonly regimeSelecionado = signal<RegimeImposto | null>(null);
  readonly quantidadeDependentes = signal<number | null>(null);
  readonly mensagemErroDependentes = signal('');
  /** O modal começa fechado e é aberto pelo botão "Veja o comparativo completo". */
  readonly openModal = signal(false);
  /** IR do benefício mensal da etapa 2 (GET /v1/requerimentos/{id}/tributacao/beneficio); nula se falhou. */
  readonly simulacao = signal<TributacaoSimulada | null>(null);
  /** IR da antecipação (GET .../tributacao/antecipacao); nula sem antecipação na etapa 2. */
  readonly simulacaoAntecipacao = signal<TributacaoSimulada | null>(null);
  /** Percentual de antecipação escolhido na etapa 2; sem ele (ou com 0%), os cards não mostram a antecipação. */
  readonly percentualAntecipacao = signal<number | null>(null);
  readonly simulando = signal(false);
  /** Só a resposta da última simulação pedida vale (o dropdown pode mudar antes da anterior voltar). */
  private ultimaSimulacao = 0;

  /** Um card por regime devolvido pela API; o bruto é o mesmo nos dois. */
  protected readonly modalidades = computed<ModalidadeImpostoOpcao[]>(() => {
    const simulacao = this.simulacao();
    if (!simulacao) return [];
    const simulacaoAntecipacao = this.simulacaoAntecipacao();
    const percentual = this.percentualAntecipacao();

    return [...simulacao.regimes]
      .sort((a, b) => ORDEM_DOS_REGIMES.indexOf(a.regime) - ORDEM_DOS_REGIMES.indexOf(b.regime))
      .map((regime) => ({
        value: IR_POR_REGIME[regime.regime],
        titulo: TITULO_POR_REGIME[regime.regime],
        antecipacao: antecipacaoDoRegime(simulacaoAntecipacao, regime.regime, percentual),
        beneficioBrutoMensal: simulacao.valorBruto,
        impostoRenda: regime.valorIr,
        valorLiquido: regime.valorLiquido,
      }));
  });
  protected readonly simulacaoIndisponivel = computed(
    () => !this.simulando() && this.simulacao() === null,
  );
  protected readonly dependentesOptions = DEPENDENTES_OPTIONS;
  protected readonly aliquotasRegressivas = ALIQUOTAS_REGRESSIVAS;

  /** Só segue com os valores calculados, o regime escolhido e a quantidade de dependentes informada. */
  protected readonly podeContinuar = computed(
    () =>
      this.simulacao() !== null &&
      this.regimeSelecionado() !== null &&
      this.quantidadeDependentes() !== null,
  );

  async ngOnInit(): Promise<void> {
    this.loaderService.showLoader('Carregando...');
    try {
      const requerimento = await this.requerimentoState.carregar();
      if (requerimento.tributacao) {
        this.preencherFormulario(requerimento.tributacao);
      }
      this.percentualAntecipacao.set(requerimento.beneficio?.percentualAntecipacaoSaldo ?? null);
      await this.simular(requerimento.id);
    } catch (erro) {
      this.router.navigate([rotaDoErroRequerimento(erro) ?? ROTA_PAGINA_INDISPONIVEL]);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  /** Recalcula os cards (troca de dependentes ou "Tentar novamente" depois de uma falha). */
  protected async recalcularSimulacao(): Promise<void> {
    const requerimento = this.requerimentoState.requerimento();
    if (!requerimento) return;

    this.loaderService.showLoader('Calculando...');
    try {
      await this.simular(requerimento.id);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  protected formatarMoeda(valor: number): string {
    return formatoMoeda.format(valor);
  }

  protected formatarValor(valor: number): string {
    return formatoValor.format(valor);
  }

  protected onDependentesChange(event: Event): void {
    const valor = (event.target as HTMLElement & { value: string }).value;
    const quantidade = valor === '' ? null : Number(valor);
    const mudou = quantidade !== this.quantidadeDependentes();
    this.quantidadeDependentes.set(quantidade);
    this.mensagemErroDependentes.set('');
    if (mudou && quantidade !== null) {
      void this.recalcularSimulacao();
    }
  }

  showModal() {
    this.openModal.set(true);
    afterNextRender(() => void this.reservarAlturaDoModal(), { injector: this.injector });
  }

  closeModal() {
    this.openModal.set(false);
    this.liberarAlturaDoModal();
  }

  /**
   * A página roda num iframe que o iframe-resizer ajusta à altura do conteúdo, e o modal (fixo)
   * não conta nessa altura. Quando a página é mais baixa que o modal, o iframe fica baixo e corta
   * o modal. Por isso, enquanto ele está aberto, o body ganha a altura do painel como mínimo
   * e o iframe-resizer cresce o iframe.
   */
  private async reservarAlturaDoModal(): Promise<void> {
    const modal = this.modalComparativo()?.nativeElement;
    await modal?.updateComplete;

    const painel = modal?.shadowRoot?.querySelector<HTMLElement>('[part="modal"]');
    if (!painel || !this.openModal()) return;

    document.body.style.minHeight = `${painel.offsetHeight + RESPIRO_DO_MODAL}px`;
    window.parentIFrame?.resize();
  }

  private liberarAlturaDoModal(): void {
    if (!document.body.style.minHeight) return;

    document.body.style.minHeight = '';
    window.parentIFrame?.resize();
  }

  onReturn() {
    this.router.navigate(['/requerimento-beneficio/configuracao-beneficio']);
  }

  async onSubmit() {
    const regime = this.regimeSelecionado();
    const dependentes = this.quantidadeDependentes();
    if (regime === null || dependentes === null) {
      return;
    }

    const requerimento = this.requerimentoState.requerimento();
    if (!requerimento) {
      this.router.navigate(['/requerimento-beneficio']);
      return;
    }

    this.loaderService.showLoader('Salvando...');
    try {
      const atualizado = await this.requerimentoService.salvar(requerimento.id, {
        tributacao: toTributacaoPayload({
          regime: REGIME_POR_IR[regime],
          qtdDependentes: dependentes,
        }),
      });
      this.requerimentoState.definir(atualizado);
      this.router.navigate(['/requerimento-beneficio/beneficiarios']);
    } catch (erro) {
      this.tratarErroAoSalvar(erro);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  private preencherFormulario(tributacao: Tributacao): void {
    if (tributacao.regime) {
      this.regimeSelecionado.set(IR_POR_REGIME[tributacao.regime]);
    }
    this.quantidadeDependentes.set(tributacao.qtdDependentes);
  }

  /**
   * IR do benefício e, com antecipação na etapa 2, o da antecipação. Em sequência, não em paralelo: o simulador grava a
   * projeção por participante e as duas chamadas se atropelam. Os erros são tratados aqui: quem chama não precisa de
   * try/catch.
   */
  private async simular(id: number): Promise<void> {
    const chamada = ++this.ultimaSimulacao;
    const dependentes = this.quantidadeDependentes();
    const percentual = this.percentualAntecipacao();
    this.simulando.set(true);
    try {
      const simulacao = await this.requerimentoService.simularTributacaoBeneficio(id, dependentes);
      const simulacaoAntecipacao =
        percentual && percentual > 0
          ? await this.requerimentoService.simularTributacaoAntecipacao(id, dependentes)
          : null;
      if (chamada === this.ultimaSimulacao) {
        this.simulacao.set(simulacao);
        this.simulacaoAntecipacao.set(simulacaoAntecipacao);
      }
    } catch (erro) {
      if (chamada === this.ultimaSimulacao) {
        this.simulacao.set(null);
        this.simulacaoAntecipacao.set(null);
        this.tratarErroAoSimular(erro);
      }
    } finally {
      if (chamada === this.ultimaSimulacao) this.simulando.set(false);
    }
  }

  private tratarErroAoSimular(erro: unknown): void {
    const status = erro instanceof HttpErrorResponse ? erro.status : null;

    // O requerimento já foi carregado, então o 404 aqui é do simulador, não do requerimento.
    if (status !== null && STATUS_SIMULACAO_NA_TELA.has(status)) {
      this.toastService.show(mensagemComDetalhe(erro, MENSAGEM_ERRO_SIMULAR), 'error');
      return;
    }

    this.router.navigate([rotaDoErroRequerimento(erro) ?? ROTA_PAGINA_INDISPONIVEL]);
  }

  private tratarErroAoSalvar(erro: unknown): void {
    const rota = rotaDoErroRequerimento(erro);
    if (rota) {
      this.router.navigate([rota]);
      return;
    }

    const destinos = {
      qtdDependIr: (mensagens: string[]) => this.mensagemErroDependentes.set(mensagens[0] ?? ''),
    };
    distribuirErrosDaApi(erro, destinos, MENSAGEM_ERRO_SALVAR).forEach((mensagem) =>
      this.toastService.show(mensagem, 'error'),
    );
  }
}
